<script lang="ts" setup>
import { computed, PropType, ref } from "vue";
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmIconView,
  TirPmModal,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmPlate,
  TirPmRadio,
} from "tir-components";
import { NeutralInformation, DimondIcon } from "tir-style-system/icons/solid";
import { AdminModel, SaveAdminsModel, UserModel } from "../../models";
import { AdapterService } from "../../adapter";
import { RouteLocationRaw, useRouter } from "vue-router";
import { CurrentUser } from "tir-pm-user-core";

/**Адаптер */
const adapter = new AdapterService();
/**Роутер */
const router = useRouter();

// #region defineProps
const props = defineProps({
  /**Список администраторов */
  admins: {
    type: Array as PropType<Array<AdminModel>>,
    required: true,
  },
  /**ID проекта */
  projectId: {
    type: Number,
    required: true,
  },
});
// #endregion defineProps

// #region defineEmits
const emit = defineEmits<{
  /**Событие успешного удаления админа */
  onSuccessDelete: [adminId: number];
}>();
// #endregion defineEmits

// #region refs
/**Админ на удаление */
const admin = ref<UserModel>();
/**Загрузка */
const isLoading = ref(false);
/**Сохранить пользователя в команде */
const keepInTeam = ref<"keep" | "no-keep">("keep");
// #endregion refs

// #region computed
const routes = computed<Record<"PerformerTasks" | "ObserverTasks", string> | undefined>(() => {
  if (!admin.value) return;

  const baseRoute: RouteLocationRaw = {
    name: "employee-tasks",
    params: {
      projectId: props.projectId,
      employeeId: admin.value.Id,
    },
  };

  return {
    PerformerTasks: router.resolve({
      ...baseRoute,
      query: {
        role: "performer",
      },
    }).href,
    ObserverTasks: router.resolve({
      ...baseRoute,
      query: {
        role: "observer",
      },
    }).href,
  };
});
// #endregion computed

// #region Функции
/**Открыть модальное окно */
const open = (_admin: UserModel) => {
  admin.value = _admin;
};
/**Закрыть модальное окно */
const close = () => {
  admin.value = undefined;
};
/**Удалить админа */
const deleteAdmin = async () => {
  if (!admin.value) return;

  const admins = props.admins
    .filter((_admin) => _admin.Id !== admin.value.Id)
    .map((admin) => ({
      Id: admin.Id,
      CanCustomizeOrder: admin.CanCustomizeOrder,
    }));
  isLoading.value = true;
  const request = new SaveAdminsModel({
    ProjectId: props.projectId,
    Admins: admins,
    RemoveQueueTasksAccess: keepInTeam.value === "no-keep",
    RemoveTaskObserver: keepInTeam.value === "no-keep",
    RemoveTaskPerformer: keepInTeam.value === "no-keep",
  });
  const resp = await adapter.saveProjectAdmins(request);
  isLoading.value = false;
  
  if (resp.IsSuccess) {
    emit("onSuccessDelete", admin.value.Id);
    TirPmNotificationEventBus.emit(
      "add",
      new TirPmNotificationModel({
        title: "Успешное редактирование",
        type: "success",
        group: "general",
        life: 1000,
        closable: true,
        callbackButton: () => {},
      }),
    );
    close();
  } else {
    TirPmNotificationEventBus.emit(
      "add",
      new TirPmNotificationModel({
        title: resp.ErrorMessage ?? "Произошла ошибка",
        type: "error",
        group: "general",
        life: 5000,
        closable: true,
        callbackButton: () => {},
      }),
    );
  }
};
// #endregion Функции

// #region defineExpose
defineExpose({
  open,
});
// #endregion defineExpose
</script>

<template>
  <slot :open="open" />
  <TirPmModal
    classes="delete-project-admin-modal"
    :is-with-back="false"
    :is-visible="!!admin"
    @close="close"
  >
    <template #title>
      <div class="delete-project-admin-modal__header">
        <TirPmIconView
          :image-load-url="admin.Photo"
          :is-rounded="true"
          :size="40"
          :color-code="admin.AvatarColor"
        >
          <template #mainSlot>
            {{ admin.AvatarInitials }}
          </template>
        </TirPmIconView>
        <span class="delete-project-admin-modal__username">
          {{ admin.Initials }}
        </span>
      </div>
    </template>
    <template #content>
      <div class="delete-project-admin-modal__content">
        <TirPmPlate :is-show="true" variant="negative">
          <template #leftAddon>
            <NeutralInformation class="delete-project-admin-modal__plate-icon icon-red" />
          </template>
          <template #rightAddon>
            <DimondIcon class="delete-project-admin-modal__plate-second-icon" />
          </template>
          <template #text>
            <span class="delete-project-admin-modal__plate-text-row"
              >Роль <span class="bold">Администратора</span> проекта будет аннулирована. </span
            >
            <span class="delete-project-admin-modal__plate-text-row"
              >Является исполнителем в тикетах:
              <a :href="routes.PerformerTasks" target="_blank">список</a></span
            >
            <span class="delete-project-admin-modal__plate-text-row"
              >Является наблюдателем в тикетах:
              <a :href="routes.ObserverTasks" target="_blank">список</a></span
            >
          </template>
        </TirPmPlate>
        <div class="delete-project-admin-modal__radio-list">
          <TirPmRadio
            name="delete-project-admin-modal__radio-list"
            value="keep"
            v-model="keepInTeam"
          >
            <template #mainSign> Оставить в командах всех рабочих областей: </template>
            <template #hintSign>
              останется доступ во все очереди для просмотра личных тикетов: где является Автором /
              Исполнителем / Наблюдателем
            </template>
          </TirPmRadio>
          <TirPmRadio
            name="delete-project-admin-modal__radio-list"
            value="no-keep"
            v-model="keepInTeam"
          >
            <template #mainSign>
              Исключить из команд всех рабочих областей (авторство в тикетах будет сохранено):
            </template>
            <template #hintSign>
              <ul class="delete-project-admin-modal__radio-hint-list">
                <li>доступ во все очереди будет аннулирован</li>
                <li>будет исключен из всех тикетов как исполнитель / наблюдатель</li>
              </ul>
            </template>
          </TirPmRadio>
        </div>
        <p class="delete-project-admin-modal__description">
          Выдать/аннулировать участие/права можно в соответствующих подразделах настроек каждой
          рабочей области/очереди
        </p>
      </div>
    </template>
    <template #footer>
      <TirPmButton
        :variant="TirPmButtonVariantEnum.PrimaryState"
        :size="TirPmButtonSizeEnum.Regular"
        :is-loading="isLoading"
        @click="deleteAdmin"
      >
        Продолжить
      </TirPmButton>
      <TirPmButton
        :variant="TirPmButtonVariantEnum.SecondaryState"
        :size="TirPmButtonSizeEnum.Regular"
        @click="close"
      >
        Отмена
      </TirPmButton>
    </template>
  </TirPmModal>
</template>

<style lang="scss" scoped>
.delete-project-admin-modal {
  &__header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  &__content {
    margin-top: 0.75rem;
  }
  &__plate-icon {
    width: 1.5rem;
    height: 1.5rem;
    color: var(--status-negative, #ec2e14);
  }
  &__plate-second-icon {
    width: 1rem;
    height: 1rem;
  }
  &__plate-text-row {
    display: block;
    color: var(--text-primary, #272d37);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;

    .bold {
      font-weight: 500;
    }
  }
  &__radio-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1rem;
  }
  &__radio-hint-list {
    margin: 0;
    padding: 0 0 0 1rem;
  }
  &__description {
    margin: 1rem 0 0.75rem 0;
    color: var(--text-primary, #272d37);
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5rem;
  }
}
</style>

<style lang="scss">
.delete-project-admin-modal {
  .modal__footer {
    border-top: 0.063rem solid var(--neutral-20, #d4d5d7);
  }
}
</style>
